import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { Slider } from '@vindr/ui';
import { Icon } from '../';

import './CinePlayer.css';
import classNames from 'classnames';

export type CinePlayerProps = {
  className: string;
  isPlaying: boolean;
  minFrameRate: number;
  maxFrameRate: number;
  stepFrameRate: number;
  frameRate: number;
  onFrameRateChange: (value: number) => void;
  onPlayPauseChange: (value: boolean) => void;
  onClose: () => void;
};

const fpsButtonClassNames =
  'cursor-pointer text-primary-active active:text-primary-light hover:bg-customblue-300 w-4 flex items-center justify-center';

const CinePlayer: React.FC<CinePlayerProps> = ({
  className,
  isPlaying,
  minFrameRate,
  maxFrameRate,
  stepFrameRate,
  frameRate: defaultFrameRate,
  onFrameRateChange,
  onPlayPauseChange,
  onClose,
}) => {
  const [frameRate, setFrameRate] = useState(defaultFrameRate);
  const debouncedSetFrameRate = debounce(onFrameRateChange, 300);

  const getPlayPauseIconName = () => (isPlaying ? 'icon-pause' : 'icon-play');

  const handleSetFrameRate = (frameRate: number) => {
    if (frameRate < minFrameRate || frameRate > maxFrameRate) {
      return;
    }
    setFrameRate(frameRate);
    debouncedSetFrameRate(frameRate);
  };

  return (
    <div
      className={classNames(
        className,
        'flex select-none items-center gap-2 rounded border border-secondary-light/60 bg-primary-dark px-2 py-2'
      )}
      style={{ bottom: '0.5rem' }}
    >
      <Icon
        name={getPlayPauseIconName()}
        className="hover:bg-customblue-300 cursor-pointer text-white hover:rounded active:text-primary-light"
        onClick={() => onPlayPauseChange(!isPlaying)}
      />
      <Tooltip
        placement="top"
        className="group/fps cine-fps-range-tooltip"
        title={
          <Slider
            className="w-40"
            value={frameRate}
            onChange={handleSetFrameRate}
            min={minFrameRate}
            max={maxFrameRate}
            step={stepFrameRate}
            tooltip={{ open: false }}
          />
        }
      >
        <div className="flex h-6 items-stretch gap-1 rounded border border-secondary-light">
          <div
            className={`${fpsButtonClassNames} rounded-l`}
            onClick={() => handleSetFrameRate(frameRate - 1)}
          >
            <Icon name="arrow-left-small" />
          </div>
          <div className="w-11 text-center text-sm leading-[22px] text-white group-hover/fps:text-primary-light">
            {`${frameRate} FPS`}
          </div>
          <div
            className={`${fpsButtonClassNames} rounded-r`}
            onClick={() => handleSetFrameRate(frameRate + 1)}
          >
            <Icon name="arrow-right-small" />
          </div>
        </div>
      </Tooltip>
      {/* <Icon
        name="icon-close"
        className="cursor-pointer text-primary-active active:text-primary-light hover:bg-customblue-300 hover:rounded"
        onClick={onClose}
      /> */}
    </div>
  );
};

const noop = () => {};

CinePlayer.defaultProps = {
  isPlaying: false,
  minFrameRate: 1,
  maxFrameRate: 90,
  stepFrameRate: 1,
  frameRate: 24,
  onPlayPauseChange: noop,
  onFrameRateChange: noop,
  onClose: noop,
};

CinePlayer.propTypes = {
  /** Minimum value for range slider */
  minFrameRate: PropTypes.number.isRequired,
  /** Maximum value for range slider */
  maxFrameRate: PropTypes.number.isRequired,
  /** Increment range slider can "step" in either direction */
  stepFrameRate: PropTypes.number.isRequired,
  frameRate: PropTypes.number.isRequired,
  /** 'true' if playing, 'false' if paused */
  isPlaying: PropTypes.bool.isRequired,
  onPlayPauseChange: PropTypes.func,
  onFrameRateChange: PropTypes.func,
  onClose: PropTypes.func,
};

export default CinePlayer;
